import React, { useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { CaseStatusData } from '../types/CaseStatusData';
import { PaymentProvider } from '../types/PaymentTypes';
import PaymentRow from './PaymentRow';
import PaymentProviderPicker from './PaymentProviderPicker';
import { useCaseStatusLanguage } from '../../../../routes/CaseStatus/CaseStatusLanguageProvider';

type Props = {
  data: CaseStatusData;
  payWithoutLoginEnabled: boolean;
  providers: PaymentProvider[];
  providersLoading: boolean;
  confirmLoading: boolean;
  onPickProvider: () => void;
  onConfirmProvider: (provider: PaymentProvider) => void;
  hideRemainingAmount?: boolean;
  hidePayNow?: boolean;
  embedded?: boolean;
};

const PaymentInfoCard = ({
  data,
  payWithoutLoginEnabled,
  providers,
  providersLoading,
  confirmLoading,
  onPickProvider,
  onConfirmProvider,
  hideRemainingAmount = false,
  hidePayNow = false,
  embedded = false,
}: Props) => {
  const { t, fmtCurrency } = useCaseStatusLanguage();
  const [showPicker, setShowPicker] = useState(false);

  const handlePayNowClick = () => {
    setShowPicker(true);
    onPickProvider();
  };

  if (showPicker) {
    return (
      <PaymentProviderPicker
        providers={providers}
        isLoading={providersLoading}
        confirmLoading={confirmLoading}
        totalBalance={data.totalBalance}
        currency={data.currency}
        onConfirm={onConfirmProvider}
        onBack={() => setShowPicker(false)}
      />
    );
  }

  const content = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AccountBalanceIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          {t('PaymentInformation')}
        </Typography>
      </Box>

      {!hideRemainingAmount && (
        <PaymentRow
          label={t('RemainingAmount')}
          value={fmtCurrency(data.totalBalance, data.currency)}
          copyValue={data.totalBalance}
          large
        />
      )}
      {data.kid && (
        <PaymentRow label={t('PaymentReferenceKID')} value={data.kid} copyValue={data.kid} />
      )}
      {data.accountNumber && (
        <PaymentRow
          label={t('AccountNumber')}
          value={data.accountNumber}
          copyValue={data.accountNumber}
        />
      )}

      {payWithoutLoginEnabled && !hidePayNow && (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="primary" fullWidth onClick={handlePayNowClick}>
            {t('PayNow')}
          </Button>
        </Box>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return <Paper elevation={1}>{content}</Paper>;
};

export default PaymentInfoCard;
